import type { TransportDiagnostics, TransportWaitingFor } from '@hanamikoji/p2p';

export interface IceConfigurationDiagnostics {
  configured: boolean;
  serverCount: number;
  hasTurn: boolean;
  mode: 'built-in-stun' | 'stun-only' | 'turn';
  summary: string;
}

export interface ConnectionDiagnosticLine {
  label: string;
  value: string;
  tone: 'normal' | 'warning' | 'danger';
}

export function buildIceConfigurationDiagnostics(rawValue: string | undefined, servers: RTCIceServer[]): IceConfigurationDiagnostics {
  const configured = Boolean(rawValue?.trim());
  const hasTurn = servers.some(server => readUrls(server).some(url => /^turns?:/i.test(url)));
  const mode = !configured ? 'built-in-stun' : hasTurn ? 'turn' : 'stun-only';
  const summary = mode === 'turn'
    ? `已配置 VITE_P2P_ICE_SERVERS（${servers.length} 组，包含 TURN）。`
    : mode === 'stun-only'
      ? `已配置 VITE_P2P_ICE_SERVERS（${servers.length} 组，仅 STUN；严格 NAT 下仍可能失败）。`
      : '未配置 VITE_P2P_ICE_SERVERS；当前使用内置公共 STUN，跨网络连接不保证成功。';
  return { configured, serverCount: servers.length, hasTurn, mode, summary };
}

export function buildConnectionDiagnosticLines(diagnostics: TransportDiagnostics | null): ConnectionDiagnosticLine[] {
  if (!diagnostics) {
    return [{ label: '连接诊断', value: '尚未创建连接。', tone: 'normal' }];
  }
  return [
    { label: 'connectionState', value: diagnostics.connectionState, tone: stateTone(diagnostics.connectionState) },
    { label: 'iceConnectionState', value: diagnostics.iceConnectionState, tone: stateTone(diagnostics.iceConnectionState) },
    { label: 'signalingState', value: diagnostics.signalingState, tone: stateTone(diagnostics.signalingState) },
    { label: 'DataChannel', value: diagnostics.dataChannelState, tone: stateTone(diagnostics.dataChannelState) },
    { label: '当前等待', value: describeWaitingFor(diagnostics.waitingFor), tone: diagnostics.waitingFor ? 'warning' : 'normal' },
  ];
}

export function describeWaitingFor(waitingFor: TransportWaitingFor): string {
  if (waitingFor === 'offer') return '等待 Host offer';
  if (waitingFor === 'answer') return '等待 Player answer';
  if (waitingFor === 'connection') return '等待 P2P/DataChannel 建立';
  return '无';
}

export function getFriendlyConnectionFailure(diagnostics: TransportDiagnostics | null): string | null {
  if (!diagnostics) return null;
  if (diagnostics.connectionState === 'failed' || diagnostics.iceConnectionState === 'failed') {
    return 'P2P 连接失败。请确认双方页面仍打开，优先尝试同一 Wi-Fi 或手机热点；跨网络失败时需要配置 TURN。';
  }
  if (diagnostics.dataChannelState === 'closed' || diagnostics.connectionState === 'closed') {
    return '连接已关闭。请返回恢复入口，重新交换新的邀请和 answer。';
  }
  return null;
}

function readUrls(server: RTCIceServer): string[] {
  return Array.isArray(server.urls) ? server.urls : [server.urls];
}

function stateTone(state: string): ConnectionDiagnosticLine['tone'] {
  if (state === 'failed' || state === 'closed') return 'danger';
  if (state === 'disconnected' || state === 'checking' || state === 'connecting' || state === 'new') return 'warning';
  return 'normal';
}
